import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { getSurvey, getQuota, getSubmissionByToken, submitSurvey, updateSubmissionByToken } from '../lib/api';
import type { SurveyConfig } from '../components/editor/types';
import { computeVisibleQuestions } from '../lib/display-logic-bridge';
import QuestionInput from '../components/runner/QuestionInput';
import SignaturePad from '../components/runner/SignaturePad';
import ImageLightbox from '../components/ImageLightbox';

type Mode = 'fill' | 'done' | 'edit';

type Props = {
  mode?: Mode;
};

type QuotaState = {
  total: { limit: number | null; used: number; remaining: number | null };
  options: Array<{
    questionId: string;
    optionValue: string;
    limit: number;
    used: number;
    remaining: number;
    onQuotaFull: 'jumpNext' | 'end';
  }>;
};

export default function Runner({ mode = 'fill' }: Props) {
  const { id, token } = useParams<{ id: string; token: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [config, setConfig] = useState<SurveyConfig | null>(null);
  const [surveyId, setSurveyId] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaState | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [signature, setSignature] = useState<string | null>(null);
  const [prefaceChecked, setPrefaceChecked] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);
  const [titleImage, setTitleImage] = useState<{ src: string; alt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const quotaPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 加载
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (mode === 'edit' && token) {
          // 改答：先取 submission，再取 survey
          const subResp = await getSubmissionByToken(token);
          const sub = subResp.submission;
          setSurveyId(sub.surveyId);
          setAnswers(JSON.parse(sub.payload || '{}'));
          setSignature(sub.signature);
          const sr = await getSurvey(sub.surveyId);
          if (cancelled) return;
          setConfig(sr.config);
        } else if (mode === 'fill' && id) {
          setSurveyId(id);
          const sr = await getSurvey(id);
          if (cancelled) return;
          setConfig(sr.config);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || '加载失败');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, id, token]);

  // 配额轮询（10 秒一次刷新，让"已满"状态实时反映）
  useEffect(() => {
    if (mode === 'done' || !surveyId) return;
    let cancelled = false;
    const fetchQuota = () =>
      getQuota(surveyId)
        .then((q: QuotaState) => !cancelled && setQuota(q))
        .catch(() => {});
    fetchQuota();
    quotaPollRef.current = setInterval(fetchQuota, 10000);
    return () => {
      cancelled = true;
      if (quotaPollRef.current) clearInterval(quotaPollRef.current);
    };
  }, [mode, surveyId]);

  // done 页：从 location.state 取 editToken + resultPage + redirectUrl
  if (mode === 'done') {
    const state = (location.state ?? {}) as {
      editToken?: string;
      surveyId?: string;
      documentUrl?: string | null;
      editAnswerEnabled?: boolean;
      resultPage?: { title?: string; body?: string };
      redirectUrl?: { enabled?: boolean; url?: string };
    };
    return (
      <DonePage
        editToken={state.editToken}
        surveyId={state.surveyId ?? id ?? null}
        documentUrl={state.documentUrl ?? null}
        editAnswerEnabled={!!state.editAnswerEnabled}
        resultPage={state.resultPage}
        redirectUrl={state.redirectUrl}
      />
    );
  }

  // 仅"问卷加载失败"用全屏错误；提交类错误在表单底部内联提示
  if (error && !config) {
    return (
      <div className="runner">
        <div className="runner-error">加载失败：{error}</div>
        <Link to="/" className="btn">返回首页</Link>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="runner">
        <div className="runner-loading">加载中…</div>
      </div>
    );
  }

  // 停止收集
  if (config.settings?.stopCollect) {
    return (
      <div className="runner">
        <div className="runner-stopped">
          <h1>问卷已停止收集</h1>
          <p>如有疑问请联系主办方。</p>
        </div>
      </div>
    );
  }

  // 每人限填一次：本机已提交过直接拦（服务端另有设备去重兜底）
  let submittedToken: string | null = null;
  try { submittedToken = surveyId ? localStorage.getItem(`wj_submitted_${surveyId}`) : null; } catch {}
  if (mode === 'fill' && config.settings?.oncePerUser && submittedToken) {
    const canEdit = !!config.settings?.editAnswer?.enabled;
    return (
      <div className="runner">
        <div className="runner-stopped">
          <h1>您已填写过本问卷</h1>
          <p>本问卷每人限填一次，感谢您的参与。</p>
          {canEdit && (
            <p style={{ marginTop: 16 }}>
              <Link className="btn btn-primary" to={`/edit/${submittedToken}`}>查看 / 修改我的提交</Link>
            </p>
          )}
        </div>
      </div>
    );
  }

  // 填写有效期（timeLimit）
  const tl = config.settings?.timeLimit;
  if (tl?.enabled && (tl.start || tl.end)) {
    const now = Date.now();
    const startMs = tl.start ? new Date(tl.start).getTime() : -Infinity;
    const endMs = tl.end ? new Date(tl.end).getTime() : Infinity;
    if (now < startMs) {
      return (
        <div className="runner">
          <div className="runner-stopped">
            <h1>问卷尚未开放</h1>
            <p>开放时间：{tl.start ? new Date(tl.start).toLocaleString('zh-CN') : '未指定'}</p>
          </div>
        </div>
      );
    }
    if (now > endMs) {
      return (
        <div className="runner">
          <div className="runner-stopped">
            <h1>问卷已结束</h1>
            <p>截止时间：{tl.end ? new Date(tl.end).toLocaleString('zh-CN') : '未指定'}</p>
          </div>
        </div>
      );
    }
  }

  // 总配额已满
  if (quota?.total && quota.total.limit !== null && quota.total.remaining === 0) {
    return (
      <div className="runner">
        <div className="runner-stopped">
          <h1>名额已满</h1>
          <p>本问卷名额已收集完毕，感谢您的关注。</p>
        </div>
      </div>
    );
  }

  // 选项配额满处理
  const fullOptionsByQuestion = computeFullOptions(quota);
  const fullEnd = isAnyFullEnd(fullOptionsByQuestion); // 任一选项 onQuotaFull=end 且满额 → 整问卷结束

  if (fullEnd) {
    return (
      <div className="runner">
        <div className="runner-stopped">
          <h1>该问卷已结束</h1>
          <p>某选项配额已满，按规则提前结束。</p>
        </div>
      </div>
    );
  }

  // 计算可见题目（选项满 + jumpNext 的题视为跳过：从 config 临时移除？不，让用户继续，但 disable 那选项）
  // 简化：visible 题正常显示，但满额选项 disable
  const visibleIds = computeVisibleQuestions(config, answers);
  const visible = config.questions.filter((q) => visibleIds.includes(q.id));

  const setAns = (qid: string, v: any) => {
    setAnswers((a) => {
      const next = { ...a, [qid]: v };
      return next;
    });
  };

  // 前置页：协议/须知正文/配图 + 独立前置填写项（settings.preface.questions）+ 标记「前置页」的题 +（可选）签名，一页完成
  const preface = config.settings?.preface;
  const sigCfg = config.settings?.signature;
  const sigOnPreface = !!(sigCfg?.enabled && sigCfg?.onPreface);
  const prefaceExtraQs = (preface?.questions ?? []) as any[];
  const prefaceQs = [...prefaceExtraQs, ...visible.filter((q) => q.onPreface)];
  const hasPrefacePage = mode === 'fill' && (!!(preface?.title || preface?.body || preface?.image) || prefaceQs.length > 0 || sigOnPreface);

  if (hasPrefacePage && !prefaceChecked) {
    const missingPreface = prefaceQs.filter((q) => q.required && !isAnsweredFor(q, answers[q.id]));
    const sigMissing = !!(sigOnPreface && sigCfg?.required && !signature);
    const canContinue = (!preface?.requireCheck || ackChecked) && missingPreface.length === 0 && !sigMissing;
    return (
      <div className="runner">
        <div className="preface-page">
          {(preface?.title || preface?.body || preface?.image) && (
            <>
              <h1>{preface?.title ?? '前置须知'}</h1>
              {preface?.image && <img src={preface.image} alt="前置页图片" className="preface-image" />}
              {preface?.body && <pre className="preface-body">{preface.body}</pre>}
            </>
          )}
          {prefaceQs.length > 0 && (
            <ol className="runner-questions" style={{ marginTop: 16 }}>
              {prefaceQs.map((q, i) => (
                <li key={q.id} className="rq-item">
                  <div className="rq-no">{i + 1}</div>
                  <div className="rq-main">
                    <div className="rq-title">
                      {q.title}
                      {q.required && <span className="req">*</span>}
                    </div>
                    <QuestionInput
                      question={q}
                      value={answers[q.id]}
                      onChange={(v) => setAns(q.id, v)}
                      fullOptions={fullOptionsByQuestion[q.id]}
                    />
                  </div>
                </li>
              ))}
            </ol>
          )}
          {sigOnPreface && (
            <div className="sig-block">
              <h3>签署人签名（{sigCfg?.mode === 'upload' ? '上传' : '手写'}）</h3>
              <SignaturePad mode={sigCfg?.mode ?? 'draw'} value={signature} onChange={setSignature} />
            </div>
          )}
          {preface?.requireCheck && (
            <label className="checkbox" style={{ marginTop: 14 }}>
              <input type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} />
              我已阅读并理解以上内容
            </label>
          )}
          <div className="runner-actions" style={{ marginTop: 16 }}>
            <button className="btn btn-primary" disabled={!canContinue} onClick={() => setPrefaceChecked(true)}>
              继续 →
            </button>
          </div>
          {!canContinue && (missingPreface.length > 0 || sigMissing) && (
            <div className="hint" style={{ marginTop: 8 }}>
              请先完成本页{missingPreface.length > 0 ? '必填项' : ''}{missingPreface.length > 0 && sigMissing ? '与' : ''}{sigMissing ? '签名' : ''}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 主页面题目：前置页题不重复显示（改答模式则全部集中在主页面，含独立前置填写项）
  const mainQs = mode === 'edit' ? [...prefaceExtraQs, ...visible] : visible.filter((q) => !q.onPreface);

  // 必填校验：可见 + required 的题必须有非空答案（矩阵要求每行都答，附件要求已上传）；独立前置填写项一并校验
  const missingRequired = [...prefaceExtraQs, ...visible].filter((q) => q.required && !isAnsweredFor(q, answers[q.id]));
  const sigRequired = config.settings?.signature?.required && !signature;
  const canSubmit = missingRequired.length === 0 && !sigRequired;

  // 邀请链接：/s/:id?invite=令牌 → 提交时带 x-invite-token 头
  const inviteToken = new URLSearchParams(location.search).get('invite') ?? undefined;

  const handleSubmit = async () => {
    if (!canSubmit || !surveyId) return;
    setSubmitting(true);
    setError(null);
    try {
      // 改答模式：原地更新（保留 edit_token；飞书侧收到 event=update，不产生重复行）
      const r =
        mode === 'edit' && token
          ? // signature 显式传 null 才能在改答时清除签名（undefined 会被后端视作"保持原值"）
            await updateSubmissionByToken(token, { answers, signature })
          : await submitSurvey(
              surveyId,
              { answers, signature: signature ?? undefined },
              inviteToken ? { inviteToken } : undefined,
            );
      if (r.ok) {
        // 每人限填一次：记住本机已提交（拦截重复进入，同时保留改答入口）
        if (mode !== 'edit' && config.settings?.oncePerUser && r.editToken) {
          try { localStorage.setItem(`wj_submitted_${surveyId}`, r.editToken); } catch {}
        }
        navigate(`/s/${encodeURIComponent(surveyId)}/done`, {
          state: {
            editToken: r.editToken,
            surveyId,
            documentUrl: r.documentUrl ?? null,
            editAnswerEnabled: !!config.settings?.editAnswer?.enabled,
            resultPage: config.settings?.resultPage,
            redirectUrl: config.settings?.redirectUrl,
          },
        });
      } else {
        // 配额满 / 已停止 / IP 限次 / 邀请链接 / 服务端强校验
        const errMap: Record<string, string> = {
          total_quota_full: '名额已满，无法提交',
          option_quota_full: `选项配额已满：${r.detail?.optionValue ?? ''}`,
          submit_limit_full: `您已达到本问卷的提交次数上限（${r.detail?.limit ?? ''} 次）`,
          collect_stopped: '问卷已停止收集',
          collect_not_open: '问卷尚未开放',
          collect_ended: '问卷已结束',
          invite_required: '本问卷仅凭邀请链接填写',
          invite_invalid: '邀请令牌无效，请使用主办方提供的完整链接',
          already_submitted: '您已填写过本问卷（每人限填一次）',
          device_required: '请使用浏览器打开本问卷填写',
          edit_not_allowed: '本问卷未开启改答功能',
          edit_token_expired: '改答链接已过期',
          missing_required: '还有必填题未作答',
          signature_required: '请先完成电子签名',
          not_found: '提交记录不存在或已被删除',
          invalid_config: '问卷配置无效，无法提交',
        };
        setError(errMap[r.error] || `提交失败：${r.error}`);
        // 刷新配额状态
        try {
          const q = await getQuota(surveyId);
          setQuota(q);
        } catch {}
      }
    } catch (e: any) {
      setError(e?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="runner">
      <div className="runner-head">
        <Link to="/" className="runner-back">← 返回问卷管理</Link>
        <h1>{config.header.mainTitle}</h1>
        {config.header.subTitle && <p>{config.header.subTitle}</p>}
      </div>

      <ol className="runner-questions">
        {mainQs.map((q, i) => (
          <li key={q.id} className="rq-item">
            <div className="rq-no">{i + 1}</div>
            <div className="rq-main">
              <div className="rq-title">
                {q.title}
                {q.required && <span className="req">*</span>}
              </div>
              {(q.image ?? q.descriptionImage) && (
                <button
                  type="button"
                  className="rq-opt-img-btn"
                  onClick={() => setTitleImage({ src: (q.image ?? q.descriptionImage)!, alt: q.title })}
                  title="点击查看大图"
                  style={{ margin: '6px 0 10px', display: 'block' }}
                >
                  <img src={q.image ?? q.descriptionImage} alt={q.title} style={{ maxWidth: 280, maxHeight: 200, borderRadius: 8, display: 'block' }} />
                </button>
              )}
              <QuestionInput
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAns(q.id, v)}
                fullOptions={fullOptionsByQuestion[q.id]}
              />
            </div>
          </li>
        ))}
      </ol>

      {config.settings?.signature?.enabled && (mode === 'edit' || !sigOnPreface) && (
        <div className="sig-block">
          <h3>电子签（{config.settings.signature.mode === 'draw' ? '手写' : '上传'}）</h3>
          <SignaturePad
            mode={config.settings.signature.mode ?? 'draw'}
            value={signature}
            onChange={setSignature}
          />
        </div>
      )}

      {error && <div className="runner-error">{error}</div>}
      {missingRequired.length > 0 && (
        <div className="hint hint-bad">还有 {missingRequired.length} 题必填未答</div>
      )}
      {titleImage && <ImageLightbox src={titleImage.src} alt={titleImage.alt} onClose={() => setTitleImage(null)} />}

      <div className="runner-actions">
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? '提交中…' : mode === 'edit' ? '保存修改' : '提交问卷'}
        </button>
      </div>
    </div>
  );
}

function DonePage({
  editToken,
  surveyId,
  documentUrl,
  editAnswerEnabled,
  resultPage,
  redirectUrl,
}: {
  editToken?: string;
  surveyId: string | null;
  documentUrl?: string | null;
  editAnswerEnabled?: boolean;
  resultPage?: { title?: string; body?: string };
  redirectUrl?: { enabled?: boolean; url?: string };
}) {
  const title = resultPage?.title || '提交成功 ✓';
  const body = resultPage?.body || '感谢您的填写，您的回答已记录。';
  const redirect = redirectUrl?.enabled && redirectUrl?.url ? redirectUrl.url : null;

  return (
    <div className="runner">
      <div className="runner-done">
        <h1>{title}</h1>
        <pre className="result-body">{body}</pre>
        {documentUrl && (
          <div className="edit-link-block">
            <p>您签署的文件已生成并存档，可随时查看或下载留存：</p>
            <p>
              <a className="btn" href={documentUrl} target="_blank" rel="noreferrer">查看签署文档</a>{' '}
              <a className="btn" href={`${documentUrl}?download=1`}>下载 PDF</a>
            </p>
          </div>
        )}
        {editToken && editAnswerEnabled && (
          <div className="edit-link-block">
            <p>您可凭以下专属链接修改本次提交（请妥善保存）：</p>
            <code className="edit-link">
              {window.location.origin}/edit/{editToken}
            </code>
          </div>
        )}
        <div className="runner-actions">
          {redirect && (
            <a href={redirect} className="btn btn-primary" target="_blank" rel="noreferrer">
              继续前往 →
            </a>
          )}{' '}
          {surveyId && (
            <Link to={`/s/${encodeURIComponent(surveyId)}`} className="btn">
              再填一份
            </Link>
          )}{' '}
          <Link to="/" className="btn">
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}

function computeFullOptions(quota: QuotaState | null): Record<string, Array<{ value: string; limit: number; used: number; onQuotaFull: 'jumpNext' | 'end' }>> {
  const out: Record<string, any[]> = {};
  if (!quota) return out;
  for (const opt of quota.options) {
    if (opt.remaining > 0) continue;
    if (!out[opt.questionId]) out[opt.questionId] = [];
    out[opt.questionId].push({
      value: opt.optionValue,
      limit: opt.limit,
      used: opt.used,
      onQuotaFull: opt.onQuotaFull,
    });
  }
  return out;
}

function isAnyFullEnd(map: Record<string, Array<{ onQuotaFull: string }>>): boolean {
  for (const qid in map) {
    for (const o of map[qid]) {
      if (o.onQuotaFull === 'end') return true;
    }
  }
  return false;
}

function isAnsweredFor(q: { type?: string; params?: any }, value: any): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (q.type === 'matrix') {
    const rows: string[] = q.params?.rows ?? [];
    if (!value || typeof value !== 'object') return false;
    return rows.every((r) => value[r] !== undefined && value[r] !== null && value[r] !== '');
  }
  if (q.type === 'file') {
    return !!(typeof value === 'object' && (value.dataUrl || value.attachment));
  }
  // 文本题答案配图：{ text, images } 需有文字或至少一张图
  if (typeof value === 'object' && (typeof value.text === 'string' || Array.isArray(value.images))) {
    return String(value.text ?? '').trim() !== '' || (Array.isArray(value.images) && value.images.length > 0);
  }
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}
